import React from 'react';
import { Container } from '../../components/HOC/container';
import { Title } from '../../components/title/title';
import { BackButton } from '../../components/BackButton';
import { LanguageSelector } from '../../components/LanguageSelector';
import { Footer } from '../../components/Footer';
import style from './Policy.module.scss';
import { useTranslation } from '../../shared/hooks/useTranslation';
import { getBcpLocale } from '../../shared/dateFormat';

export function Policy() {
  const { t, language } = useTranslation();

  return (
    <div className={style.pageWrap}>
    <Container>
      <div className={style.policy}>
        <header className={style.header}>
          <BackButton />
          <Title className={style.title}>{t.policyTitle}</Title>
          <LanguageSelector />
        </header>

        <div className={style.content}>
          <div className={style.documentHeader}>
            <h1>{t.policyTitle}</h1>
            <p className={style.documentSubtitle}>
              {t.documentSubtitle}
            </p>
            <div className={style.documentMeta}>
              <p><strong>{t.version}:</strong> 1.0</p>
              <p><strong>{t.lastUpdated}</strong> {new Date().toLocaleDateString(getBcpLocale(language))}</p>
            </div>
          </div>

          <div className={style.documentBody}>
            <section className={style.section}>
              <h2>{t.generalProvisions}</h2>
              <h3>{t.scopeOfApplication}</h3>
              <p>{t.policyDefinition}</p>
              
              <h3>{t.acceptanceOfTerms}</h3>
              <p>{t.acceptanceText}</p>
            </section>

            <section className={style.section}>
              <h2>{t.dataCollection}</h2>
              <h3>{t.dataCategories}</h3>
              <p>{t.weCollect}</p>
              <ul>
                <li><strong>{t.iinPlaceholder}</strong> — {t.iinDescription}</li>
                <li><strong>{t.fullName}</strong> — {t.nameDescription}</li>
                <li><strong>{t.phoneHint.split(' ')[0]} {t.phoneHint.split(' ')[1]}</strong> — {t.phoneDescription}</li>
                <li><strong>{t.medicalInfoDescription.split(' — ')[0]}</strong> — {t.medicalInfoDescription.split(' — ')[1]}</li>
                <li><strong>{t.appointmentDataDescription.split(' — ')[0]}</strong> — {t.appointmentDataDescription.split(' — ')[1]}</li>
              </ul>
            </section>

            <section className={style.section}>
              <h2>{t.dataPurpose}</h2>
              <h3>{t.processingPurposes}</h3>
              <p>{t.personalDataProcessed}</p>
              <ul>
                <li><strong>{t.systemOperation.split(' — ')[0]}</strong> — {t.systemOperation.split(' — ')[1]}</li>
                <li><strong>{t.appointmentManagement.split(' — ')[0]}</strong> — {t.appointmentManagement.split(' — ')[1]}</li>
                <li><strong>{t.notifications.split(' — ')[0]}</strong> — {t.notifications.split(' — ')[1]}</li>
                <li><strong>{t.serviceImprovement.split(' — ')[0]}</strong> — {t.serviceImprovement.split(' — ')[1]}</li>
                <li><strong>{t.legalCompliance.split(' — ')[0]}</strong> — {t.legalCompliance.split(' — ')[1]}</li>
              </ul>
            </section>

            <section className={style.section}>
              <h2>{t.legalBasis}</h2>
              <h3>{t.processingBasis}</h3>
              <p>{t.processingBasisText}</p>
              <ol>
                <li><strong>{t.subjectConsent.split(' — ')[0]}</strong> — {t.subjectConsent.split(' — ')[1]}</li>
                <li><strong>{t.contractExecution.split(' — ')[0]}</strong> — {t.contractExecution.split(' — ')[1]}</li>
                <li><strong>{t.legalRequirements.split(' — ')[0]}</strong> — {t.legalRequirements.split(' — ')[1]}</li>
              </ol>
            </section>

            <section className={style.section}>
              <h2>{t.dataRetention}</h2>
              <h3>{t.retentionPeriods}</h3>
              <p>{t.retentionText}</p>
              <p><strong>{t.activeRecords.split(' — ')[0]}</strong> — {t.activeRecords.split(' — ')[1]}</p>
              <p><strong>{t.treatmentHistory.split(' — ')[0]}</strong> — {t.treatmentHistory.split(' — ')[1]}</p>
              <p><strong>{t.userAccount.split(' — ')[0]}</strong> — {t.userAccount.split(' — ')[1]}</p>
            </section>

            <section className={style.section}>
              <h2>{t.dataSecurity}</h2>
              <h3>{t.securityMeasures}</h3>
              <p>{t.securityText}</p>
              <p>{t.appliedMeasures}</p>
              <ul>
                <li>{t.dataEncryption}</li>
                <li>{t.accessControl}</li>
                <li>{t.securityMonitoring}</li>
                <li>{t.systemUpdates}</li>
                <li>{t.staffTraining}</li>
              </ul>
            </section>

            <section className={style.section}>
              <h2>{t.dataSharing}</h2>
              <h3>{t.transferConditions}</h3>
              <p>{t.transferText}</p>
              <p><strong>{t.medicalInstitutions.split(' — ')[0]}</strong> — {t.medicalInstitutions.split(' — ')[1]}</p>
              <p><strong>{t.governmentBodies.split(' — ')[0]}</strong> — {t.governmentBodies.split(' — ')[1]}</p>
              <p><strong>{t.otherCases.split(' — ')[0]}</strong> — {t.otherCases.split(' — ')[1]}</p>
            </section>

            <section className={style.section}>
              <h2>{t.yourRights}</h2>
              <h3>{t.dataSubjectRights}</h3>
              <p>{t.youHaveRight}</p>
              <ul>
                <li><strong>{t.rightToInfo.split(' — ')[0]}</strong> — {t.rightToInfo.split(' — ')[1]}</li>
                <li><strong>{t.rightToCorrection.split(' — ')[0]}</strong> — {t.rightToCorrection.split(' — ')[1]}</li>
                <li><strong>{t.rightToWithdraw.split(' — ')[0]}</strong> — {t.rightToWithdraw.split(' — ')[1]}</li>
                <li><strong>{t.rightToAppeal.split(' — ')[0]}</strong> — {t.rightToAppeal.split(' — ')[1]}</li>
              </ul>
            </section>

            <section className={style.section}>
              <h2>{t.policyChanges}</h2>
              <h3>{t.changesProcedure}</h3>
              <p>{t.changesText}</p>
              <p>{t.whenMakingChanges}</p>
              <ul>
                <li>{t.usersReceiveNotifications}</li>
                <li>{t.effectiveDateIndicated}</li>
                <li>{t.continuedUseMeansAgreement}</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </Container>
      <Footer />
    </div>
  );
} 